import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Star, Settings, X, Trophy, Calendar } from 'lucide-react'
import { sportsAPI } from '../../services/api'
import toast from 'react-hot-toast'
import { format } from 'date-fns'

interface SportsWidgetProps {
  widget: any
  isExpanded: boolean
}

export default function SportsWidget({ widget, isExpanded }: SportsWidgetProps) {
  const [selectedView, setSelectedView] = useState<'all' | 'favorites' | 'live'>('all')
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [selectedSport, setSelectedSport] = useState('all')
  const [selectedLeague, setSelectedLeague] = useState('all')
  const [showSettings, setShowSettings] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const queryClient = useQueryClient()

  // Fetch scores
  const { data: scores } = useQuery({
    queryKey: ['sports-scores', selectedSport, selectedLeague, selectedDate],
    queryFn: async () => {
      if (selectedView !== 'all') return []
      const { data } = await sportsAPI.getScores(
        selectedSport !== 'all' ? selectedSport : undefined,
        selectedLeague !== 'all' ? selectedLeague : undefined,
        selectedDate
      )
      return data
    },
    enabled: selectedView === 'all',
  })

  // Fetch live games
  const { data: liveGames } = useQuery({
    queryKey: ['sports-live'],
    queryFn: async () => {
      const { data } = await sportsAPI.getLiveGames()
      return data
    },
    enabled: selectedView === 'live',
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Fetch favorite team games
  const { data: favoriteGames } = useQuery({
    queryKey: ['sports-favorite-games'],
    queryFn: async () => {
      const { data } = await sportsAPI.getFavoriteGames()
      return data
    },
    enabled: selectedView === 'favorites',
  })

  // Fetch leagues
  const { data: leagues } = useQuery({
    queryKey: ['sports-leagues'],
    queryFn: async () => {
      const { data } = await sportsAPI.getLeagues()
      return data
    },
  })

  // Fetch favorite teams
  const { data: favoriteTeams } = useQuery({
    queryKey: ['sports-favorite-teams'],
    queryFn: async () => {
      const { data } = await sportsAPI.getFavorites()
      return data
    },
  })

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await sportsAPI.refreshScores(
        selectedSport !== 'all' ? selectedSport : undefined,
        selectedLeague !== 'all' ? selectedLeague : undefined
      )
      queryClient.invalidateQueries({ queryKey: ['sports-scores'] })
      queryClient.invalidateQueries({ queryKey: ['sports-live'] })
      queryClient.invalidateQueries({ queryKey: ['sports-favorite-games'] })
      toast.success('Scores refreshed!')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to refresh scores')
    } finally {
      setRefreshing(false)
    }
  }

  const getGameStatus = (game: any) => {
    if (game.status === 'live' || game.is_live) {
      return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full animate-pulse">LIVE</span>
    }
    if (game.status === 'final') {
      return <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">FINAL</span>
    }
    if (game.game_time) {
      return <span className="text-xs text-gray-600">{format(new Date(game.game_time), 'h:mm a')}</span>
    }
    return null
  }

  const renderGame = (game: any) => (
    <div
      key={game.id}
      className="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition bg-white"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase">{game.league}</span>
          {game.is_live && <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>}
        </div>
        {getGameStatus(game)}
      </div>

      <div className="space-y-2">
        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {game.away_team_logo && (
              <img src={game.away_team_logo} alt="" className="w-6 h-6 object-contain" />
            )}
            <span className={`font-medium truncate ${game.status !== 'scheduled' && game.away_score > game.home_score ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
              {game.away_team_name}
            </span>
          </div>
          <span className={`text-lg font-bold ${game.status !== 'scheduled' && game.away_score > game.home_score ? 'text-gray-900' : 'text-gray-700'}`}>
            {game.away_score !== null ? game.away_score : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {game.home_team_logo && (
              <img src={game.home_team_logo} alt="" className="w-6 h-6 object-contain" />
            )}
            <span className={`font-medium truncate ${game.status !== 'scheduled' && game.home_score > game.away_score ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
              {game.home_team_name}
            </span>
          </div>
          <span className={`text-lg font-bold ${game.status !== 'scheduled' && game.home_score > game.away_score ? 'text-gray-900' : 'text-gray-700'}`}>
            {game.home_score !== null ? game.home_score : '-'}
          </span>
        </div>
      </div>

      {game.venue && (
        <div className="mt-2 text-xs text-gray-500 truncate">
          📍 {game.venue}
        </div>
      )}
    </div>
  )

  const currentGames = selectedView === 'live' ? liveGames : 
                       selectedView === 'favorites' ? favoriteGames : 
                       scores

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-gray-200 space-y-2">
        {/* Top Row: View Tabs and Actions */}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            <button
              onClick={() => setSelectedView('all')}
              className={`px-3 py-1 text-xs rounded transition ${
                selectedView === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedView('live')}
              className={`px-3 py-1 text-xs rounded transition flex items-center gap-1 ${
                selectedView === 'live' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className={selectedView === 'live' ? '' : 'opacity-0'}>●</span> Live
            </button>
            <button
              onClick={() => setSelectedView('favorites')}
              className={`px-3 py-1 text-xs rounded transition flex items-center gap-1 ${
                selectedView === 'favorites' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Star size={12} /> Favorites
            </button>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Refresh scores"
            >
              <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Settings"
            >
              <Settings size={14} />
            </button>
          </div>
        </div>

        {/* Filters Row */}
        {selectedView === 'all' && (
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 rounded"
            />
            
            {leagues && leagues.length > 0 && (
              <>
                <select
                  value={selectedSport}
                  onChange={(e) => {
                    setSelectedSport(e.target.value)
                    setSelectedLeague('all')
                  }}
                  className="px-2 py-1 text-xs border border-gray-300 rounded"
                >
                  <option value="all">All Sports</option>
                  {Array.from(new Set(leagues.map((l: any) => l.sport))).map((sport: any) => (
                    <option key={sport} value={sport}>{sport}</option>
                  ))}
                </select>

                {selectedSport !== 'all' && (
                  <select
                    value={selectedLeague}
                    onChange={(e) => setSelectedLeague(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                  >
                    <option value="all">All Leagues</option>
                    {leagues
                      .filter((l: any) => l.sport === selectedSport)
                      .map((league: any) => (
                        <option key={league.league} value={league.league}>
                          {league.league}
                        </option>
                      ))}
                  </select>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {currentGames && currentGames.length > 0 ? (
          currentGames.map((game: any) => renderGame(game))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Trophy size={48} className="mx-auto mb-2 opacity-30" />
            <p className="mb-1">
              {selectedView === 'live' && 'No live games right now'}
              {selectedView === 'favorites' && 'No games for your favorite teams'}
              {selectedView === 'all' && 'No games scheduled for this date'}
            </p>
            {selectedView === 'all' && (
              <button
                onClick={handleRefresh}
                className="text-sm text-blue-600 hover:underline mt-2"
              >
                Load sample data
              </button>
            )}
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Sports Settings</h3>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Add Team Section */}
            <div className="mb-6">
              <h4 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Add Teams to Follow</h4>
              <AddTeamInterface 
                onTeamAdded={() => {
                  queryClient.invalidateQueries({ queryKey: ['sports-favorite-teams'] })
                  queryClient.invalidateQueries({ queryKey: ['sports-favorite-games'] })
                }}
              />
            </div>

            {/* Favorite Teams List */}
            <div>
              <h4 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Your Favorite Teams</h4>
              {favoriteTeams && favoriteTeams.length > 0 ? (
                <div className="space-y-2">
                  {favoriteTeams.map((team: any) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between p-2 rounded"
                      style={{ backgroundColor: 'var(--color-surface)' }}
                    >
                      <div className="flex items-center gap-2">
                        {team.logo_url && (
                          <img src={team.logo_url} alt="" className="w-6 h-6 object-contain" />
                        )}
                        <div>
                          <div className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{team.name}</div>
                          <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{team.league}</div>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await sportsAPI.removeFavorite(team.id)
                            queryClient.invalidateQueries({ queryKey: ['sports-favorite-teams'] })
                            queryClient.invalidateQueries({ queryKey: ['sports-favorite-games'] })
                            toast.success('Team removed from favorites')
                          } catch (error: any) {
                            toast.error('Failed to remove team')
                          }
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  No favorite teams yet. Add teams above to see their games!
                </p>
              )}
            </div>
          </div>

          <button
            onClick={() => setShowSettings(false)}
            className="mt-4 w-full px-4 py-2 rounded-lg hover:bg-gray-300 transition"
            style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-primary)' }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}

// Map league slug to sport and league name
const mapLeagueToSportAndLeague = (leagueSlug: string): { sport: string; league: string } => {
  const mapping: { [key: string]: { sport: string; league: string } } = {
    'nfl': { sport: 'football', league: 'NFL' },
    'nba': { sport: 'basketball', league: 'NBA' },
    'mlb': { sport: 'baseball', league: 'MLB' },
    'nhl': { sport: 'hockey', league: 'NHL' },
    'ncaaf': { sport: 'football', league: 'NCAAF' },
    'ncaab': { sport: 'basketball', league: 'NCAAB' },
  }
  return mapping[leagueSlug.toLowerCase()] || { sport: leagueSlug, league: leagueSlug.toUpperCase() }
}

// Add Team Interface Component
function AddTeamInterface({ onTeamAdded }: { onTeamAdded: () => void }) {
  const [selectedLeague, setSelectedLeague] = useState('nfl')
  const [searchTerm, setSearchTerm] = useState('')
  const [availableTeams, setAvailableTeams] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const handleLoadTeams = async () => {
    setLoading(true)
    try {
      // First, fetch scores to populate teams
      await sportsAPI.refreshScores()
      
      // Map league slug to sport and league
      const { sport, league } = mapLeagueToSportAndLeague(selectedLeague)
      
      // Then get teams for the league
      const { data } = await sportsAPI.getTeams(sport, league)
      setAvailableTeams(data || [])
      
      if (!data || data.length === 0) {
        toast('No teams found for this league', { icon: 'ℹ️' })
      }
    } catch (error) {
      console.error('Failed to load teams:', error)
      toast.error('Failed to load teams')
      setAvailableTeams([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddTeam = async (teamId: number) => {
    try {
      await sportsAPI.addFavorite(teamId)
      toast.success('Team added to favorites!')
      onTeamAdded()
      // Remove from available list
      setAvailableTeams(teams => teams.filter(t => t.id !== teamId))
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.error('Team already in favorites')
      } else {
        toast.error('Failed to add team')
      }
    }
  }

  const filteredTeams = availableTeams.filter(team =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.abbreviation.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-3">
      {/* League Selector */}
      <div className="flex gap-2">
        <select
          value={selectedLeague}
          onChange={(e) => setSelectedLeague(e.target.value)}
          className="flex-1 px-3 py-2 border rounded-lg text-sm"
          style={{ 
            backgroundColor: 'var(--color-surface)', 
            borderColor: 'var(--color-border)', 
            color: 'var(--color-text-primary)' 
          }}
        >
          <option value="nfl">NFL</option>
          <option value="nba">NBA</option>
          <option value="mlb">MLB</option>
          <option value="nhl">NHL</option>
          <option value="ncaaf">NCAA Football</option>
          <option value="ncaab">NCAA Basketball</option>
        </select>
        <button
          onClick={handleLoadTeams}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Load Teams'}
        </button>
      </div>

      {/* Search Teams */}
      {availableTeams.length > 0 && (
        <>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search teams..."
            className="w-full px-3 py-2 border rounded-lg text-sm"
            style={{ 
              backgroundColor: 'var(--color-surface)', 
              borderColor: 'var(--color-border)', 
              color: 'var(--color-text-primary)' 
            }}
          />

          {/* Teams List */}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filteredTeams.length > 0 ? (
              filteredTeams.map((team) => (
                <div
                  key={team.id}
                  className="flex items-center justify-between p-2 rounded hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-2">
                    {team.logo_url && (
                      <img src={team.logo_url} alt="" className="w-5 h-5 object-contain" />
                    )}
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {team.name}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}>
                      {team.abbreviation}
                    </span>
                  </div>
                  <button
                    onClick={() => handleAddTeam(team.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition"
                  >
                    <Star size={12} />
                    Add
                  </button>
                </div>
              ))
            ) : (
              <p className="text-sm text-center py-4" style={{ color: 'var(--color-text-secondary)' }}>
                {searchTerm ? 'No teams found matching your search' : 'No teams available'}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

